import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FLEX_LAYOUT_COMPONENT, COMPONENT_PROPS_TAB_PREFIX } from "constants/flowDesigner";
import { Actions, DockLocation, Model } from "flexlayout-react";
import { setActiveFlexLayoutTab } from "redux/actions/flowDesigner/tabsActionCreator";
import { SET_TO_OPEN_NODE_TAB } from "redux/constants/flowDesigner";

import { setSelectedLogNodeId } from "redux/actions/flowDesigner/flowExecutionActionCreator";

export const FlexLayoutContext = createContext(null);
export const useFlexLayout = () => useContext(FlexLayoutContext);

export const FlexLayoutContextProvider = ({ children }) => {
  const currentSavedFlow = useSelector((store) => store.designer.flows.currentSavedFlow);
  const dispatch = useDispatch();
  const isInitialMount = useRef(true);
  const previousTabId = useRef(null);
  const closeGuardsRef = useRef(new Map());

  const model = useMemo(() => {
    let layoutJson = {
      global: {},
      layout: {
        type: "row",
        children: [
          {
            type: "tabset",
            enableTabStrip: true,
            weight: 75,
            children: [{ type: "tab", name: "Flow", component: "reactflow", enableRename: false }]
          }
        ]
      }
    };

    if (currentSavedFlow?.id) {
      const saved = localStorage.getItem(`flexlayout-${currentSavedFlow.id}`);
      if (saved) {
        try {
          layoutJson = JSON.parse(saved);
        } catch {
          // ignore invalid JSON stored in localStorage
        }
      }
    }

    return Model.fromJson(layoutJson);
  }, [currentSavedFlow?.id]);

  const addLayoutTab = (tab, tabId, tabName, tabProps = {}) => {
    const firstTabset = model.getFirstTabSet();
    const existingTab = model.getNodeById(tabId);

    if (existingTab) {
      if (tabId === "startNodeProps" && tab === FLEX_LAYOUT_COMPONENT.triggerProps) {
        model.doAction(Actions.updateNodeAttributes(tabId, { component: tab, config: tabProps }));
      }
      if (tabName && existingTab.getName() !== tabName) {
        model.doAction(Actions.updateNodeAttributes(tabId, { name: tabName }));
      }
      model.doAction(Actions.selectTab(tabId));

      return;
    }

    function findNonFlowTabset(node) {
      if (
        node.getType() === "tabset" &&
        node.getChildren().length > 0 &&
        node.getChildren()[0].getComponent() !== "reactflow"
      ) {
        return node;
      }
      for (const child of node.getChildren()) {
        const found = findNonFlowTabset(child);
        if (found) return found;
      }
      return null;
    }

    const root = model.getRoot();
    let targetTabset = findNonFlowTabset(root);

    if (!targetTabset) {
      model.doAction(
        Actions.addNode(
          {
            type: "tab",
            component: tab,
            weight: 90,
            name: tabName,
            config: tabProps,
            id: tabId,
            enableRename: false
          },
          firstTabset.getId(),
          DockLocation.RIGHT,
          -1
        )
      );
      const allTabsets = model.getRoot().getChildren();
      if (allTabsets.length > 1) {
        const flowTabset = allTabsets[0];
        const newTabset = allTabsets[1];
        const newTabsetWeight = tabProps?.wide ? 50 : 35;
        model.doAction(Actions.updateNodeAttributes(flowTabset.getId(), { weight: 100 - newTabsetWeight }));
        model.doAction(Actions.updateNodeAttributes(newTabset.getId(), { weight: newTabsetWeight }));
      }
    } else {
      model.doAction(
        Actions.addNode(
          {
            type: "tab",
            component: tab,
            weight: 30,
            name: tabName,
            config: tabProps,
            id: tabId,
            enableRename: false
          },
          targetTabset.getId(),
          DockLocation.CENTER,
          -1
        )
      );
    }
    model.doAction(Actions.selectTab(tabId));
  };

  const deleteLayoutTab = (tabNodeId) => {
    model.doAction(Actions.deleteTab(tabNodeId));
  };

  const deleteMultipleLayoutTabs = (tabNodeIds) => {
    tabNodeIds.forEach((tabNodeId) => {
      model.doAction(Actions.deleteTab(tabNodeId));
    });
  };

  const checkAndCleanupStorage = (json) => {
    const root = json.layout;
    if (root.type === "row" && root.children) {
      const tabsets = root.children.filter((child) => child.type === "tabset");

      const primaryTabset = tabsets[0];
      const primaryTabs = primaryTabset?.children || [];
      const hasReactflowTab = primaryTabs.some((tab) => tab.component === "reactflow");

      if (!hasReactflowTab && currentSavedFlow?.id) {
        localStorage.removeItem(`flexlayout-${currentSavedFlow.id}`);
        return true;
      }

      if (tabsets.length === 1) {
        const tabs = tabsets[0].children || [];
        const hasOnlyFlowTab = tabs.length === 1 && tabs[0].component === "reactflow";
        const hasNoTabs = tabs.length === 0;

        if ((hasOnlyFlowTab || hasNoTabs) && currentSavedFlow?.id) {
          localStorage.removeItem(`flexlayout-${currentSavedFlow.id}`);
          return true;
        }
      }
    }
    return false;
  };

  const closeAllTabsExceptReactflow = () => {
    const json = model.toJson();
    const layout = json.layout;
    const tabsToDelete = [];

    function collectNonReactflowTabs(node) {
      if (node.type === "tab" && node.component !== "reactflow") {
        tabsToDelete.push(node.id);
      }
      if (node.children) {
        node.children.forEach((child) => collectNonReactflowTabs(child));
      }
    }

    collectNonReactflowTabs(layout);

    tabsToDelete.forEach((tabId) => {
      model.doAction(Actions.deleteTab(tabId));
    });

    dispatch({ type: SET_TO_OPEN_NODE_TAB, payload: null });

    if (currentSavedFlow?.id) {
      localStorage.removeItem(`flexlayout-${currentSavedFlow.id}`);
    }
  };

  const registerTabCloseGuard = (tabId, fn) => {
    closeGuardsRef.current.set(tabId, fn);
  };

  const unregisterTabCloseGuard = (tabId) => {
    closeGuardsRef.current.delete(tabId);
  };

  const requestCloseTab = (tabId) => {
    const guard = closeGuardsRef.current.get(tabId);
    if (!guard) return true;
    return guard();
  };

  useEffect(() => {
    const listener = () => {
      try {
        if (!currentSavedFlow?.id) return;
        const json = model.toJson();

        const wasCleanedUp = checkAndCleanupStorage(json);

        if (!wasCleanedUp) {
          localStorage.setItem(`flexlayout-${currentSavedFlow.id}`, JSON.stringify(json));
        }
      } catch {
        // ignore localStorage write errors (e.g. storage quota exceeded)
      }
    };
    if (currentSavedFlow?.id) model.addChangeListener(listener);
    return () => model.removeChangeListener(listener);
  }, [model, currentSavedFlow?.id, dispatch]);

  useEffect(() => {
    if (!currentSavedFlow?.name) return;
    const root = model.getRoot();
    const flowTab = root.getChildren()[0]?.children[0];
    if (flowTab) {
      model.doAction(
        Actions.updateNodeAttributes(flowTab.getId(), { name: currentSavedFlow.name })
      );
    }
  }, [currentSavedFlow?.name, model]);

  useEffect(() => {
    if (!model) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const onChange = (e) => {
      const allowedEventTypes = ["FlexLayout_DeleteTab", "FlexLayout_SelectTab"];
      if (!allowedEventTypes.includes(e.type)) return;

      const modelJson = model.toJson();
      const layout = modelJson.layout;

      function findActiveTabset(node) {
        if (node.type === "tabset" && node.active) {
          return node;
        }
        if (node.children) {
          for (const child of node.children) {
            const found = findActiveTabset(child);
            if (found) return found;
          }
        }
        return null;
      }

      function findFirstTabset(node) {
        if (node.type === "tabset") {
          return node;
        }
        if (node.children) {
          for (const child of node.children) {
            const found = findFirstTabset(child);
            if (found) return found;
          }
        }
        return null;
      }

      let activeTabset = findActiveTabset(layout);

      if (!activeTabset) {
        activeTabset = findFirstTabset(layout);
      }

      if (activeTabset) {
        const selectedIndex = activeTabset.selected ?? 0;
        const tabId = activeTabset.children[selectedIndex]?.id;

        if (tabId !== previousTabId.current) {
          previousTabId.current = tabId;
          dispatch(setActiveFlexLayoutTab(tabId));
          if (tabId) {
            const sanitizedId = tabId.replace(COMPONENT_PROPS_TAB_PREFIX, "").replace(/#/g, "");
            dispatch(setSelectedLogNodeId(sanitizedId));
          }
        }
      }
    };

    model.addChangeListener(onChange);
    return () => {
      model.removeChangeListener(onChange);
    };
  }, [model, dispatch]);

  return (
    <FlexLayoutContext.Provider
      value={{
        addLayoutTab,
        model,
        deleteLayoutTab,
        deleteMultipleLayoutTabs,
        closeAllTabsExceptReactflow,
        registerTabCloseGuard,
        unregisterTabCloseGuard,
        requestCloseTab
      }}
    >
      {children}
    </FlexLayoutContext.Provider>
  );
};
